/**
 * Registro único de las soluciones de Gob 24/7.
 *
 * Lo consumen el router (rutas y títulos), Header (submenú), Footer (columna del ecosistema),
 * HomeSoluciones (nombre, ruta, color y CTA) y SolucionDetalleView (las cuatro subpáginas).
 * Cambiar un nombre, un módulo o una relación se hace aquí y en ningún otro archivo.
 */
import capturaMapaCalor from '@/assets/soluciones/vision360/reportes.mp4'
import heroVision360 from '@/assets/soluciones/vision360/hero.webp'
import expediente from '@/assets/soluciones/vision360/expediente.mp4'
import vulnerabilidad from '@/assets/soluciones/vision360/vulnerabilidad.mp4'
import caracterizadorCiudadano from '@/assets/soluciones/vision360/caracterizadorCiudadano.mp4'
import caracterizadorProgramas from '@/assets/soluciones/vision360/resultadoCaracterizadorProgramas.mp4'
import programas from '@/assets/soluciones/vision360/caracterizadorProgramas.mp4'
import ai from '@/assets/soluciones/vision360/chatbot.webp'

import heroDesarrollo from '@/assets/soluciones/planesDesarrollo/hero.webp'
import interoperabilidadNacional from '@/assets/soluciones/planesDesarrollo/interoperabilidad.webp'
import estructuraJerarquica from '@/assets/soluciones/planesDesarrollo/estructuraJerarquica.mp4'
import trazabilidadPresupuestal from '@/assets/soluciones/planesDesarrollo/trazabilidadPresupuestal.webp'
import medicionImpacto from '@/assets/soluciones/planesDesarrollo/medicionImpacto.mp4'
import capacidadPredictiva from '@/assets/soluciones/planesDesarrollo/capacidadPredictiva.webp'

import heroPlanAccion from '@/assets/soluciones/planesAccion/hero.webp'
import programacion from '@/assets/soluciones/planesAccion/programacion.mp4'
import reportes from '@/assets/soluciones/planesAccion/reportes.mp4'
import validacion from '@/assets/soluciones/planesAccion/validacion.mp4'
import rpa from '@/assets/soluciones/planesAccion/rpa.webp'

import heroGestion from '@/assets/soluciones/gestionTareasProyectosEstrategicos/hero.webp'
import consolidar from '@/assets/soluciones/gestionTareasProyectosEstrategicos/consolidar.mp4'
import delegar from '@/assets/soluciones/gestionTareasProyectosEstrategicos/delegar.mp4'
import monitoreo from '@/assets/soluciones/gestionTareasProyectosEstrategicos/monitoreo.mp4'
import planificar from '@/assets/soluciones/gestionTareasProyectosEstrategicos/planificar.mp4'

/** @type {Solucion[]} */
export const SOLUCIONES = [
  {
    id: 'vision-360',
    ruta: '/vision-360',
    nombre: 'Visión 360° del Ciudadano',
    nombreCorto: 'Visión 360°',
    color: 'var(--color-primary-dark)',
    resumenIndice: 'Comprende el territorio desde la mirada del ciudadano y la prioridad social.',
    hero: {
      subtitulo: 'Comprenda la situación real de cada hogar y priorice a quien más lo necesita, sin pedirle dos veces la misma historia.',
      imagen: heroVision360
    },
    ventaja: {
      titular: 'Todas los organismos viendo la misma realidad.',
      antes: 'Cada programa guarda su propio formulario, y la persona repite su historia en cada ventanilla.',
      ahora: 'Un expediente único, un índice que ordena la prioridad y una sola conversación con el Estado.'
    },
    modulosNumerados: false,
    modulos: [
      {
        id: 'expediente-unico-digital',
        nombre: 'Expediente único digital',
        resumen: 'Historial unificado de atenciones e intervenciones sociales.',
        capacidades: [
          'Información consolidada de cada ciudadano',
          'Historial completo de atenciones',
          'Acceso desde cualquier dependencia'
        ],
        captura: expediente,
        alt: ''
      },
      {
        id: 'indice-de-vulnerabilidad',
        nombre: 'Índice de Vulnerabilidad indexado',
        resumen: 'Medición objetiva en cada registro para priorizar recursos.',
        capacidades: [
          'Priorización automática de recursos',
          'Enfoque en la población más vulnerable',
          'Medición objetiva y estandarizada'
        ],
        captura: vulnerabilidad,
        alt: ''
      },
      {
        id: 'caracterizador-ciudadano',
        nombre: 'Caracterizador del Ciudadano',
        resumen: 'Perfil social, económico y de salud de cada persona.',
        capacidades: [
          'Salud, educación, vivienda e ingresos en un solo perfil',
          'Enfoque diferencial: víctimas, discapacidad, etnia y gestación',
          'Índice de Vulnerabilidad calculado en cada caracterización'
        ],
        captura: caracterizadorCiudadano,
        alt: ''
      },
      {
        id: 'caracterizador-de-programas',
        nombre: 'Caracterizador de programas de atención social',
        resumen: 'Formularios adaptables por programas sociales.',
        capacidades: [
          'Formularios configurables por programa',
          'Adaptable a diferentes intervenciones',
          'Actualización en tiempo real'
        ],
        captura: caracterizadorProgramas,
        alt: ''
      },
      {
        id: 'geolocalizacion-y-mapas-de-calor',
        nombre: 'Geolocalización y mapas de calor',
        resumen: 'Visualización territorial de la vulnerabilidad.',
        capacidades: [
          'Ubicación georreferenciada de cada ciudadano',
          'Mapas de calor territoriales',
          'Identificación de zonas de mayor vulnerabilidad'
        ],
        captura: capturaMapaCalor,
        alt: 'Mapa de calor de la distribución territorial de beneficiarios en Gob 24/7'
      },
      {
        id: 'gestor-de-procesos-de-atencion',
        nombre: 'Gestor de procesos de atención social',
        resumen: 'Creación y seguimiento de flujos digitales para la atención social del ciudadano.',
        capacidades: [
          'Diseño y automatización de flujos etapa por etapa',
          'Metas de tiempo estándar para cada fase',
          'Alertas preventivas ante retenciones o estancamientos'
        ],
        captura: programas,
        alt: ''
      },
      {
        id: 'interoperabilidad-e-inteligencia-de-datos',
        nombre: 'Interoperabilidad e inteligencia de datos',
        resumen: 'Potenciamos la gestión con tecnologías de vanguardia.',
        capacidades: [
          'Chatbot de atención: respuesta inmediata a ciudadanos 24/7',
          'Agentes de IA: consultas inteligentes como Sisbén',
          'Alertas tempranas: detección preventiva de crisis'
        ],
        captura: ai,
        alt: ''
      }
    ],
    relacionadas: [
      { id: 'planes-desarrollo', frase: 'Cada atención social suma a una meta del Plan de Desarrollo.' },
      { id: 'planes-accion', frase: 'El programa que atiende a esa persona tiene actividades, responsables y evidencias' }
    ],
    cierre: { titular: 'Sepa a quién atender primero, antes de que sea urgente.' }
  },
  {
    id: 'planes-desarrollo',
    ruta: '/planes-desarrollo',
    nombre: 'Planes de Desarrollo',
    nombreCorto: 'Planes de Desarrollo',
    color: 'var(--color-primary-dark)',
    resumenIndice: 'Hace visible la ejecución, la inversión y el cumplimiento de objetivos.',
    hero: {
      subtitulo: 'Vea qué metas avanzan, cuáles se retrasan y dónde actuar',
      imagen: heroDesarrollo
    },
    ventaja: {
      titular: 'Corrija el rumbo antes de reportar, no después.',
      antes: 'El avance se conoce en el corte del periodo. Para entonces el resultado ya está escrito.',
      ahora: 'Metas, indicadores y cada peso del presupuesto a la vista todos los días.'
    },
    modulosNumerados: false,
    modulos: [
      {
        id: 'interoperabilidad-nacional',
        nombre: 'Interoperabilidad nacional y reporte en tiempo real',
        resumen: 'Gestión activa y automatizada frente a entes de control y sistemas del DNP.',
        capacidades: [
          'Interoperabilidad automática con las plataformas nacionales obligatorias: SisPT (planeación), PIIP (inversión pública) y CUIPO (presupuesto)',
          'Gestión activa frente al reporte final: gestione y corrija el avance físico y financiero en tiempo real antes del cierre',
          'Generación de reportes a un clic: rendición de cuentas sin horas de reprocesos ni duplicación de tareas'
        ],
        captura: interoperabilidadNacional,
        alt: ''
      },
      {
        id: 'estructura-jerarquica',
        nombre: 'Estructura jerárquica',
        resumen: 'Visualización por líneas estratégicas, sectores, programas y metas.',
        capacidades: ['Desglose completo por niveles', 'Visibilidad de toda la estructura'],
        captura: estructuraJerarquica,
        alt: ''
      },
      {
        id: 'trazabilidad-presupuestal',
        nombre: 'Trazabilidad presupuestal',
        resumen: 'Conexión directa entre presupuesto ejecutado y avance físico.',
        capacidades: ['Ejecución presupuestal en tiempo real', 'Avance físico frente al financiero'],
        captura: trazabilidadPresupuestal,
        alt: ''
      },
      {
        id: 'medicion-de-impacto',
        nombre: 'Medición de impacto',
        resumen: 'Metas de producto frente a metas de resultado.',
        capacidades: ['Comparativa de metas', 'Impacto real en la comunidad'],
        captura: medicionImpacto,
        alt: ''
      },
      {
        id: 'capacidad-predictiva',
        nombre: 'Capacidad predictiva',
        resumen: 'Inteligencia para proyectar desviaciones y alertar.',
        capacidades: ['Alertas preventivas', 'Corrección temprana de desviaciones'],
        captura: capacidadPredictiva,
        alt: ''
      }
    ],
    relacionadas: [
      { id: 'planes-accion', frase: 'Cada meta del plan baja a actividades de inversión por vigencia.' },
      { id: 'vision-360', frase: 'Priorice la inversión social sabiendo dónde está la mayor necesidad.' }
    ],
    cierre: { titular: 'Cumpla lo que prometió y demuéstrelo con datos.' }
  },
  {
    id: 'planes-accion',
    ruta: '/planes-accion',
    nombre: 'Planes de Acción',
    nombreCorto: 'Planes de Acción',
    color: 'var(--color-primary-dark)',
    resumenIndice: 'Convierte la estrategia en operaciones claras, rápidas y medibles.',
    hero: {
      subtitulo: 'Convierta cada meta del plan en el trabajo de esta semana: con responsable, con fecha y con evidencia.',
      imagen: heroPlanAccion
    },
    ventaja: {
      titular: 'El plan cuatrienal, traducido en tareas con responsable.',
      antes: 'Actividades, entregables y evidencias repartidos entre correos y hojas de cálculo.',
      ahora: 'Una sola programación anual: actividades, responsables y evidencias en el mismo lugar.'
    },
    modulosNumerados: false,
    modulos: [
      {
        id: 'programacion-anual-e-inversion',
        nombre: 'Programación anual e inversión (POAI)',
        resumen: 'Desglose del Plan de Desarrollo por vigencias anuales.',
        capacidades: ['Planificación anual detallada', 'Asignación por secretarías', 'Inversión alineada a metas'],
        captura: programacion,
        alt: ''
      },
      {
        id: 'validacion-de-ejecucion',
        nombre: 'Validación de ejecución',
        resumen: 'Seguimiento mensual del avance de actividades.',
        capacidades: ['Seguimiento mensual de entregables', 'Control financiero en tiempo real', 'Alertas de desviación'],
        captura: validacion,
        alt: ''
      },
      {
        id: 'reportes-e-informes-automaticos',
        nombre: 'Reportes e informes automáticos',
        resumen: 'Generación en un solo clic para entes de control.',
        capacidades: [
          'Reportes estandarizados (MIPG, PIIP, CUIPO)',
          'Exportación en múltiples formatos',
          'Informes automáticos programados'
        ],
        captura: reportes,
        alt: ''
      },
      {
        id: 'agentes-de-interoperabilidad',
        nombre: 'Agentes de interoperabilidad (RPA)',
        resumen: 'Automatizaciones inteligentes para eliminar la duplicidad de carga manual de datos.',
        capacidades: [
          'Automatización de datos: sincronización entre sistemas',
          'Reducción de tiempo: hasta 70 % menos de carga',
          'Cero errores manuales: precisión en la información'
        ],
        captura: rpa,
        alt: ''
      }
    ],
    relacionadas: [
      { id: 'planes-desarrollo', frase: 'Las actividades alimentan el avance de las metas del plan.' },
      { id: 'gestion-tareas-proyectos', frase: 'Lo que no es inversión también necesita responsable y seguimiento.' }
    ],
    cierre: { titular: 'Que cada dependencia sepa qué le toca hoy.' }
  },
  {
    id: 'gestion-tareas-proyectos',
    ruta: '/gestion-tareas-proyectos',
    nombre: 'Gestión de Tareas y Proyectos Estratégicos',
    nombreCorto: 'Gestión de Tareas y Proyectos Estratégicos',
    color: 'var(--color-primary-dark)',
    resumenIndice: 'Coordina el trabajo diario, los responsables y los hitos estratégicos en tiempo real.',
    hero: {
      subtitulo: 'Organice el trabajo del equipo de gobierno: tareas, responsables y compromisos en un solo lugar.',
      imagen: heroGestion
    },
    ventaja: {
      titular: 'Delegue la tarea. Gob 24/7 le hace el seguimiento.',
      antes: 'Delegar toma un minuto. Perseguir lo delegado consume la semana.',
      ahora: 'Estados que se actualizan solos, alertas oportunas y evidencias revisadas en el mismo lugar.'
    },
    // El orden es el flujo real de trabajo: por eso solo esta solución numera sus módulos.
    modulosNumerados: true,
    modulos: [
      {
        id: 'planificar',
        nombre: 'Planificar',
        resumen: 'Defina objetivos, tareas, responsables y fechas.',
        capacidades: [
          'Tareas y subtareas por proyecto estratégico',
          'Fechas y entregables visibles',
          'Priorización por impacto'
        ],
        captura: planificar,
        alt: ''
      },
      {
        id: 'delegar',
        nombre: 'Delegar',
        resumen: 'Distribuya el trabajo entre secretarías y funcionarios.',
        capacidades: [
          'Asignación por secretaría, dependencia y funcionario',
          'Responsables y equipos definidos',
          'Responsabilidades sin ambigüedades'
        ],
        captura: delegar,
        alt: ''
      },
      {
        id: 'monitorear',
        nombre: 'Monitorear',
        resumen: 'Consulte avances y actúe frente a las desviaciones.',
        capacidades: [
          'Seguimiento en tiempo real de actividades, compromisos e hitos',
          'Avance actualizado por tarea',
          'Alertas de vencimiento y prevención de cuellos de botella'
        ],
        captura: monitoreo,
        alt: ''
      },
      {
        id: 'consolidar',
        nombre: 'Consolidar',
        resumen: 'Obtenga evidencia del cumplimiento y reporte resultados.',
        capacidades: [
          'Evidencias aprobadas o devueltas para corregir',
          'Reportes por dependencia',
          'Resultados visibles para el gobierno'
        ],
        captura: consolidar,
        alt: ''
      }
    ],
    relacionadas: [
      { id: 'planes-accion', frase: 'Las tareas estratégicas y las de inversión se ven en la misma agenda.' },
      { id: 'planes-desarrollo', frase: 'Los compromisos del equipo empujan las metas del plan.' }
    ],
    cierre: { titular: 'Que nada importante dependa de que alguien se acuerde.' }
  }
]

/**
 * @param {string} id
 * @returns {Solucion|null}
 */
export function solucionPorId(id) {
  return SOLUCIONES.find((solucion) => solucion.id === id) || null
}
